/* @moldura padrao */
/**
 * page.tsx (Minha Área → Notificações) — o que me alcança, e em quais aparelhos.
 *
 * ─── O NOME É "NOTIFICAÇÕES", e a decisão é de vocabulário ─────────────────
 * Fechada em 28/08/2026, depois de o app chamar três coisas diferentes pelo
 * mesmo nome. Ficou assim:
 *
 *   · **notificação** = o ALERTA — o que aparece na bandeja do sistema, no
 *     sininho e no ícone. É o nome que o Windows, o Android, o iOS e a própria
 *     API do navegador usam, e não é nosso para redefinir: a pessoa já o
 *     aprendeu no celular dela. **Esta tela é o ajuste desse canal.**
 *   · **caixa de entrada** = o LUGAR onde tudo se acumula e se age. É a frente
 *     da central, e não mora aqui: *"Minha Área é um setor que você não mexe
 *     muito — entra, mexe e sai"* (ele, 28/08). Central é visita diária.
 *   · **recado · pendência · decisão** = as três naturezas do que chega, já
 *     nomeadas no plano de 19/08.
 *
 * ─── O QUE ELA TEM HOJE, E O QUE VEM ──────────────────────────────────────
 * Hoje: os aparelhos. É a peça que existe — o canal. As preferências de "quais
 * avisos eu quero" dependem do catálogo de eventos, que é da frente da central;
 * quando ele existir, entram aqui, e é para isso que esta tela nasce.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { api, ApiError } from '@/lib/api';
import { formatarDataHora } from '@/lib/data';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

interface Aparelho {
  id: number;
  aparelho: string | null;
  criacao: string | null;
  ultimo_uso: string | null;
  ultimo_erro: string | null;
}

/** Um aviso que esta pessoa alcança, e por onde ela o recebe. */
interface Preferencia {
  evento: string;
  nome: string;
  descricao?: string | null;
  tipo: 'recado' | 'pendencia' | 'decisao';
  severidade: 'info' | 'atencao' | 'critico';
  /** `decisao` e `critico` não se desligam — a trava está no serviço, não aqui. */
  travado: boolean;
  razao_travado?: string | null;
  canais: Record<string, boolean>;
}

interface Estado {
  chave_publica: string;
  configurado: boolean;
  aparelhos: Aparelho[];
}

/**
 * A chave vem em base64url e o navegador exige bytes.
 *
 * O buffer é criado explicitamente como `ArrayBuffer` porque `Uint8Array.from`
 * devolve `Uint8Array<ArrayBufferLike>`, e `applicationServerKey` não aceita —
 * `ArrayBufferLike` inclui `SharedArrayBuffer`. O `tsc --noEmit` deixa passar;
 * o build do projeto não.
 */
function chaveParaBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const bruto = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(bruto.length));
  for (let i = 0; i < bruto.length; i += 1) bytes[i] = bruto.charCodeAt(i);
  return bytes;
}

const EH_IOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
/** No iOS, push só existe com o app instalado na tela de início. */
const INSTALADO =
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as unknown as { standalone?: boolean }).standalone === true;

/**
 * Um nome que a pessoa reconheça na lista — "Chrome no Android", não o user agent.
 *
 * O CASO DO IPHONE É EXCEÇÃO, e ela nasceu do primeiro uso real (28/08/2026):
 * ele instalou pelo Chrome e a lista mostrou "Safari no iPhone". Não é falha da
 * detecção — a Apple proíbe motor próprio no iOS, e o app instalado na tela de
 * início abre SEMPRE o WebKit do sistema, que se identifica como Safari. Como
 * no iOS o push só existe no modo instalado, ali o navegador detectado nunca
 * diz por onde a pessoa chegou: responde qual MOTOR quando ela pergunta qual
 * APP. Por isso, iPhone instalado vira "App no iPhone".
 *
 * No Android não há exceção a fazer: o app instalado É o Chrome, e "Chrome no
 * Android" é verdade — foi o que ele reconheceu de imediato ao testar.
 */
function nomeDoAparelho(): string {
  const ua = navigator.userAgent;
  if (EH_IOS && INSTALADO) return 'App no iPhone';
  const navegador = /edg/i.test(ua)
    ? 'Edge'
    : /chrome|crios/i.test(ua)
      ? 'Chrome'
      : /firefox|fxios/i.test(ua)
        ? 'Firefox'
        : /safari/i.test(ua)
          ? 'Safari'
          : 'Navegador';
  const sistema = /iphone|ipad|ipod/i.test(ua)
    ? 'iPhone'
    : /android/i.test(ua)
      ? 'Android'
      : /windows/i.test(ua)
        ? 'Windows'
        : /mac/i.test(ua)
          ? 'Mac'
          : 'computador';
  return `${navegador} no ${sistema}`;
}


export function NotificacoesPage() {
  const [estado, setEstado] = useState<Estado | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [prefs, setPrefs] = useState<Preferencia[]>([]);

  async function carregarPreferencias() {
    try {
      setPrefs(await api.get<Preferencia[]>('/avisos/preferencias'));
    } catch {
      /* a tela do canal não some porque a lista de avisos falhou */
    }
  }

  async function alternar(p: Preferencia, canal: string) {
    const antes = p.canais[canal];
    // Otimista: o interruptor responde ao dedo, e volta se o servidor recusar.
    setPrefs((ls) =>
      ls.map((x) =>
        x.evento === p.evento ? { ...x, canais: { ...x.canais, [canal]: !antes } } : x,
      ),
    );
    try {
      await api.put('/avisos/preferencias', {
        evento: p.evento,
        canal,
        ativo: !antes,
      });
    } catch (e) {
      setPrefs((ls) =>
        ls.map((x) =>
          x.evento === p.evento ? { ...x, canais: { ...x.canais, [canal]: antes } } : x,
        ),
      );
      toast.error(e instanceof ApiError ? e.message : 'Não deu para salvar.');
    }
  }

  async function carregar() {
    try {
      setEstado(await api.get<Estado>('/notificacoes/aparelhos'));
    } catch {
      /* silencioso: a tela ainda diz o que é, mesmo sem a lista */
    }
  }

  useEffect(() => {
    carregarPreferencias();
  }, []);

  useEffect(() => {
    carregar();
  }, []);

  /**
   * A PERMISSÃO SÓ PODE SER PEDIDA A PARTIR DE UM TOQUE REAL. Nada aqui pede
   * autorização ao abrir a tela: além de o Safari recusar, pedir sem a pessoa
   * ter pedido é a forma mais rápida de ela negar para sempre — e negado no
   * navegador não se desfaz pelo app, só nos ajustes do sistema.
   */
  async function ativar() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error('Este navegador não recebe notificações.');
      return;
    }
    setOcupado(true);
    try {
      const permissao = await Notification.requestPermission();
      if (permissao !== 'granted') {
        toast.error('Você recusou as notificações. Para liberar, é nos ajustes do navegador.');
        return;
      }
      const registro = await navigator.serviceWorker.ready;
      const inscricao = await registro.pushManager.subscribe({
        // Obrigatório: promete que todo push vira notificação visível. Push
        // silencioso o navegador não permite — e nem deveria.
        userVisibleOnly: true,
        applicationServerKey: chaveParaBytes(estado!.chave_publica),
      });
      const j = inscricao.toJSON() as {
        endpoint: string;
        keys: { p256dh: string; auth: string };
      };
      await api.post('/notificacoes/aparelhos', {
        endpoint: j.endpoint,
        p256dh: j.keys.p256dh,
        auth: j.keys.auth,
        aparelho: nomeDoAparelho(),
      });
      toast.success('Pronto. Este aparelho passa a receber os avisos.');
      carregar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível ativar aqui.');
    } finally {
      setOcupado(false);
    }
  }

  async function remover(a: Aparelho) {
    try {
      await api.delete(`/notificacoes/aparelhos/${a.id}`);
      carregar();
    } catch {
      toast.error('Não foi possível remover.');
    }
  }

  const jaTem = (estado?.aparelhos.length ?? 0) > 0;

  return (
    <CustomMolduraPagina
      titulo="Notificações"
      descricao="Em quais aparelhos você quer ser avisado."
    >

      <div className="card adminuiux-card mb-3 mb-lg-4">
        <div className="card-header bg-transparent">
          <h6 className="mb-0">
            <i className="bi bi-phone me-2"></i>Receber no celular
          </h6>
          <p className="text-secondary small mb-0 mt-1">
            Os avisos chegam mesmo com o aplicativo fechado.
          </p>
        </div>
        <div className="card-body">
          {/* Sem chave no servidor o botão não aparece: inscrever aqui
              produziria um aparelho que nunca recebe nada, e ninguém
              descobriria — não há erro, o aviso apenas não chega. */}
          {estado && !estado.configurado && (
            <p className="text-secondary small mb-0">
              <i className="bi bi-exclamation-triangle me-1"></i>
              O envio ainda não está ligado neste servidor.
            </p>
          )}

          {/* A INSTRUÇÃO VEM ANTES DO BOTÃO no iPhone sem o app instalado,
              porque ali o botão não tem como funcionar. Deixá-lo clicável seria
              oferecer um caminho que termina em nada. */}
          {estado?.configurado && EH_IOS && !INSTALADO ? (
            <div className="row gx-3">
              <div className="col-auto">
                <i className="bi bi-info-circle h5 avatar avatar-40 rounded bg-theme-1-subtle text-theme-1 mb-0"></i>
              </div>
              <div className="col">
                <p className="mb-1 fw-bold text-body">
                  No iPhone, primeiro instale o aplicativo
                </p>
                <p className="text-secondary small mb-0">
                  Toque em <strong>Compartilhar</strong> no Safari e depois em{' '}
                  <strong>Adicionar à Tela de Início</strong>. Abra o app por lá e
                  volte aqui — só assim o iPhone deixa os avisos chegarem.
                </p>
              </div>
            </div>
          ) : (
            estado?.configurado && (
              <button
                type="button"
                className="btn btn-theme"
                disabled={ocupado}
                onClick={ativar}
              >
                <i className="bi bi-bell me-2"></i>
                {jaTem ? 'Ativar também neste aparelho' : 'Ativar neste aparelho'}
              </button>
            )
          )}

          {jaTem && (
            <div className="mt-3">
              <p className="small text-secondary mb-2">Aparelhos que recebem hoje</p>
              {estado!.aparelhos.map((a) => (
                <div className="row gx-3 align-items-center py-2 border-top" key={a.id}>
                  <div className="col">
                    <p className="mb-0 fw-bold text-body">{a.aparelho ?? 'Aparelho'}</p>
                    {/* O último erro fica à vista: aparelho que parou de
                        receber em silêncio é o que faz a pessoa achar que o app
                        quebrou. */}
                    {a.ultimo_erro ? (
                      <p className="text-secondary small mb-0">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        O último envio falhou
                      </p>
                    ) : (
                      a.ultimo_uso && (
                        <p className="text-secondary small mb-0">
                          Último aviso em {formatarDataHora(a.ultimo_uso)}
                        </p>
                      )
                    )}
                  </div>
                  <div className="col-auto">
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-secondary"
                      title="Parar de receber neste aparelho"
                      onClick={() => remover(a)}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── QUAIS AVISOS EU QUERO ────────────────────────────────────────
          Este card substituiu, em 29/08/2026, um que dizia "o que ainda vem
          para cá". A escolha dependia do catálogo de eventos, e ele existe.

          Só aparecem os avisos que ESTA pessoa alcança: um interruptor de
          "pedido de autorização" para quem não decide nada é uma escolha que
          não muda nada — e a pessoa gasta um minuto tentando entender. */}
      {prefs.length > 0 && (
        <div className="card adminuiux-card mt-3">
          <div className="card-header bg-transparent">
            <h6 className="mb-0">
              <i className="bi bi-sliders me-2"></i>Quais avisos eu quero
            </h6>
          </div>
          <div className="card-body">
            {/* A FRASE QUE EVITA A RECLAMAÇÃO. Sem ela, quem desliga o push de
                uma pendência vai reclamar de que "continua aparecendo" — e vai
                estar certo, porque continua mesmo. */}
            <p className="small text-secondary">
              Desligar um canal cala o <strong>aviso</strong>. A pendência
              continua na Inbox, porque ela não é aviso: é o estado do seu
              trabalho.
            </p>

            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr className="small text-secondary">
                    <th className="ps-0">Aviso</th>
                    <th className="text-center">No app</th>
                    <th className="text-center">No celular</th>
                    <th className="text-center">E-mail</th>
                  </tr>
                </thead>
                <tbody>
                  {prefs.map((p) => (
                    <tr key={p.evento}>
                      <td className="ps-0">
                        <p className="mb-0">{p.nome}</p>
                        {p.travado ? (
                          /* A RAZÃO À VISTA: interruptor travado sem explicação
                             é o que faz a pessoa achar que o app quebrou. */
                          <p className="small text-secondary mb-0">
                            <i className="bi bi-lock me-1"></i>
                            {p.razao_travado}
                          </p>
                        ) : (
                          p.descricao && (
                            <p className="small text-secondary mb-0">{p.descricao}</p>
                          )
                        )}
                      </td>
                      {['app', 'push', 'email'].map((canal) => (
                        <td className="text-center" key={canal}>
                          <div className="form-check form-switch d-inline-block">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={!!p.canais[canal]}
                              disabled={p.travado}
                              onChange={() => alternar(p, canal)}
                              aria-label={`${p.nome} — ${canal}`}
                            />
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </CustomMolduraPagina>
  );
}
